import { useState, useCallback, useRef, useEffect } from "react";

// Range slider component for scores
export interface ScoreRangeSliderProps {
  min: number;
  max: number;
  minValue: number;
  maxValue: number;
  onMinChange: (value: number) => void;
  onMaxChange: (value: number) => void;
  unit: string;
  step?: number;
  showDisplay?: boolean;
}

export function ScoreRangeSlider({
  min,
  max,
  minValue,
  maxValue,
  onMinChange,
  onMaxChange,
  unit,
  step = 0.1,
  showDisplay = true,
}: ScoreRangeSliderProps) {
  const sliderRef = useRef<HTMLDivElement>(null);
  const [activeHandle, setActiveHandle] = useState<"min" | "max" | null>(null);

  const minPercentage = ((minValue - min) / (max - min)) * 100;
  const maxPercentage = ((maxValue - min) / (max - min)) * 100;

  const handleMinSliderChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseFloat(e.target.value);
      if (value <= maxValue) {
        onMinChange(value);
      } else {
        onMinChange(maxValue);
      }
    },
    [maxValue, onMinChange]
  );

  const handleMaxSliderChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseFloat(e.target.value);
      if (value >= minValue) {
        onMaxChange(value);
      } else {
        onMaxChange(minValue);
      }
    },
    [minValue, onMaxChange]
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!sliderRef.current) return;

      const rect = sliderRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const clickPercentage = (clickX / rect.width) * 100;

      const minHandlePos = minPercentage;
      const maxHandlePos = maxPercentage;

      // Determine which handle is closer
      const distanceToMin = Math.abs(clickPercentage - minHandlePos);
      const distanceToMax = Math.abs(clickPercentage - maxHandlePos);

      if (distanceToMin < distanceToMax) {
        setActiveHandle("min");
      } else {
        setActiveHandle("max");
      }
    },
    [minPercentage, maxPercentage]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!sliderRef.current || !activeHandle) return;

      const rect = sliderRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const percentage = Math.max(
        0,
        Math.min(100, (clickX / rect.width) * 100)
      );
      const value = min + (percentage / 100) * (max - min);
      const steppedValue = Math.round(value / step) * step;

      if (activeHandle === "min") {
        const newValue = Math.max(min, Math.min(steppedValue, maxValue));
        onMinChange(newValue);
      } else {
        const newValue = Math.max(minValue, Math.min(steppedValue, max));
        onMaxChange(newValue);
      }
    },
    [activeHandle, min, max, minValue, maxValue, step, onMinChange, onMaxChange]
  );

  const handleMouseUp = useCallback(() => {
    setActiveHandle(null);
  }, []);

  useEffect(() => {
    if (activeHandle) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [activeHandle, handleMouseMove, handleMouseUp]);

  return (
    <div className="space-y-3">
      {/* Range Slider */}
      <div
        ref={sliderRef}
        className="relative h-4 w-full py-2 cursor-pointer"
        onMouseDown={handleMouseDown}
      >
        {/* Background track */}
        <div className="absolute top-1/2 left-0 right-0 h-1 bg-[#e6e8e7] rounded-full -translate-y-1/2" />
        {/* Active range */}
        <div
          className="absolute top-1/2 h-1 bg-[#374741] rounded-full -translate-y-1/2"
          style={{
            left: `${minPercentage}%`,
            width: `${maxPercentage - minPercentage}%`,
          }}
        />
        {/* Min handle */}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={minValue}
          onChange={handleMinSliderChange}
          className="absolute top-1/2 left-0 w-full h-1 bg-transparent appearance-none cursor-pointer -translate-y-1/2 z-20 pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:h-3.5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:bg-transparent [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-3.5 [&::-moz-range-thumb]:h-3.5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:bg-transparent [&::-moz-range-thumb]:cursor-pointer"
          style={{
            background: "transparent",
            WebkitAppearance: "none",
            MozAppearance: "none",
          }}
        />
        {/* Max handle */}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={maxValue}
          onChange={handleMaxSliderChange}
          className="absolute top-1/2 left-0 w-full h-1 bg-transparent appearance-none cursor-pointer -translate-y-1/2 z-10 pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:h-3.5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:bg-transparent [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-3.5 [&::-moz-range-thumb]:h-3.5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:bg-transparent [&::-moz-range-thumb]:cursor-pointer"
          style={{
            background: "transparent",
            WebkitAppearance: "none",
            MozAppearance: "none",
          }}
        />
        {/* Custom handles - now clickable */}
        <div
          className="absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full border-2 border-white pointer-events-auto z-30 cursor-grab active:cursor-grabbing"
          style={{
            left: `calc(${minPercentage}% - 7px)`,
            background: "var(--color-primary-gradient)",
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
            setActiveHandle("min");
          }}
        />
        <div
          className="absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full border-2 border-white pointer-events-auto z-30 cursor-grab active:cursor-grabbing"
          style={{
            left: `calc(${maxPercentage}% - 7px)`,
            background: "var(--color-primary-gradient)",
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
            setActiveHandle("max");
          }}
        />
      </div>

      {/* Range Display */}
      {showDisplay && (
        <div className="flex justify-end">
          <span className="text-[14px] leading-[1.35] tracking-[-0.56px] text-[#374741] font-normal">
            {minValue.toFixed(unit === "Percentage" ? 0 : 1)}
            {unit === "Percentage" ? "%" : ""} -{" "}
            {maxValue.toFixed(unit === "Percentage" ? 0 : 1)}
            {unit === "Percentage" ? "%" : ""}
          </span>
        </div>
      )}
    </div>
  );
}
